import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatDate as formatDateUtil } from '../utils/dateFormat'
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api'

type ServicePurchase = {
  id: string
  number?: string
  date: string
  supplier: { id: string; name: string; club?: { id: string; name: string } }
  type?: string
  totalAmount: number
  status: string
  comment?: string
  expenseArticle?: { id: string; code: string; name: string }
  items: Array<{
    id: string
    tournamentId: string
    courtId: string
    tournament: { id: string; name: string }
    court: { name: string }
    startTime: string
    endTime: string
    hours: number
    ratePerHour: number
    amount: number
    comment?: string
  }>
}

const ServicePurchases: React.FC = () => {
  const navigate = useNavigate()
  const [purchases, setPurchases] = useState<ServicePurchase[]>([])
  const [suppliers, setSuppliers] = useState<Array<{ id: string; name: string }>>([])
  const [loading, setLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<ServicePurchase | null>(null)
  const [isCopying, setIsCopying] = useState(false)
  const [showJsonModal, setShowJsonModal] = useState(false)
  const [selectedPurchaseJson, setSelectedPurchaseJson] = useState<string>('')
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    pages: 1
  })
  const [initialFiltersLoaded, setInitialFiltersLoaded] = useState(false)
  
  // Фильтры по датам и поставщику
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [supplierFilter, setSupplierFilter] = useState('')

  // Загружаем сохраненные фильтры из localStorage
  useEffect(() => {
    const savedDateFrom = localStorage.getItem('servicePurchases_dateFrom')
    const savedDateTo = localStorage.getItem('servicePurchases_dateTo')
    const savedSupplierFilter = localStorage.getItem('servicePurchases_supplierFilter')
    
    if (savedDateFrom) {
      setDateFrom(savedDateFrom)
    }
    if (savedDateTo) {
      setDateTo(savedDateTo)
    }
    if (savedSupplierFilter) {
      setSupplierFilter(savedSupplierFilter)
    }
    setInitialFiltersLoaded(true)
  }, [])

  const loadSuppliers = async () => {
    try {
      const data = await apiGet<{ suppliers?: Array<{ id: string; name: string }> }>('/suppliers?isActive=true&limit=1000')
      setSuppliers(data.suppliers || [])
    } catch (e) {
      console.error('Ошибка загрузки поставщиков:', e)
    }
  }

  const loadPurchases = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      params.append('page', String(pagination.page))
      params.append('limit', String(pagination.limit))
      
      if (dateFrom) {
        params.append('from', dateFrom)
      }
      if (dateTo) {
        params.append('to', dateTo)
      }
      if (supplierFilter) {
        params.append('supplierId', supplierFilter)
      }
      
      const url = params.toString() ? `/service-purchases?${params.toString()}` : '/service-purchases'
      console.log('Загрузка закупок услуг:', url)
      const data = await apiGet<{
        items?: ServicePurchase[]
        pagination?: {
          page: number
          limit: number
          total: number
          pages: number
        }
      }>(url)
      console.log('Получено закупок услуг:', data.items?.length || 0)
      setPurchases(data.items || [])
      if (data.pagination) {
        setPagination(prev => ({
          ...prev,
          page: data.pagination?.page || prev.page,
          limit: data.pagination?.limit || prev.limit,
          total: data.pagination?.total || 0,
          pages: data.pagination?.pages || 1
        }))
      }
    } catch (e) {
      console.error('Ошибка загрузки закупок услуг:', e)
      setPurchases([])
    } finally {
      setLoading(false)
    }
  }

  // Загружаем поставщиков при инициализации
  useEffect(() => {
    loadSuppliers()
  }, [])

  // Загружаем данные при изменении фильтров (кроме первого рендера)
  useEffect(() => {
    if (!initialFiltersLoaded) return
    const timer = setTimeout(() => {
      loadPurchases()
    }, 100)
    return () => clearTimeout(timer)
  }, [initialFiltersLoaded, dateFrom, dateTo, supplierFilter, pagination.page, pagination.limit])

  // Функции для работы с фильтрами
  const handleDateFromChange = (value: string) => {
    setDateFrom(value)
    setPagination(prev => ({ ...prev, page: 1 }))
    localStorage.setItem('servicePurchases_dateFrom', value)
  }

  const handleDateToChange = (value: string) => {
    setDateTo(value)
    setPagination(prev => ({ ...prev, page: 1 }))
    localStorage.setItem('servicePurchases_dateTo', value)
  }

  const handleSupplierFilterChange = (value: string) => {
    setSupplierFilter(value)
    setPagination(prev => ({ ...prev, page: 1 }))
    localStorage.setItem('servicePurchases_supplierFilter', value)
  }

  const handleResetFilters = () => {
    setDateFrom('')
    setDateTo('')
    setSupplierFilter('')
    setPagination(prev => ({ ...prev, page: 1 }))
    localStorage.removeItem('servicePurchases_dateFrom')
    localStorage.removeItem('servicePurchases_dateTo')
    localStorage.removeItem('servicePurchases_supplierFilter')
  }

  const handleRefresh = () => {
    loadPurchases()
  }


  const handleEdit = (purchase: ServicePurchase) => {
    setEditing(purchase)
    setIsCopying(false)
    setShowForm(true)
  }

  const handleCopy = (purchase: ServicePurchase) => {
    // Создаем копию документа с очищенными полями
    const copiedPurchase = {
      ...purchase,
      id: '', // Очищаем ID для создания нового документа
      date: purchase.date, // Оставляем дату из оригинального документа
      status: 'DRAFT', // Устанавливаем статус "Черновик"
      comment: purchase.comment || undefined
    }
    setEditing(copiedPurchase)
    setIsCopying(true)
    setShowForm(true)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Удалить закупку услуг?')) return

    try {
      await apiDelete(`/service-purchases/${id}`)
      await loadPurchases()
    } catch (e) {
      console.error('Ошибка удаления:', e)
    }
  }

  const handleFormClose = () => {
    setShowForm(false)
    setEditing(null)
    setIsCopying(false)
    loadPurchases()
  }

  const handleCreateSupplierPayment = (purchase: ServicePurchase) => {
    // Переходим на страницу оплат поставщикам с предзаполненными данными
    const readableDate = formatDateUtil(purchase.date)
    const documentNumber = purchase.number
    const params = new URLSearchParams({
      createFrom: purchase.id,
      purchaseType: 'service', // Указываем тип закупки
      supplierId: purchase.supplier.id,
      amount: purchase.totalAmount.toString(),
      description: `Оплата по закупке услуг ${documentNumber} от ${readableDate}`,
      date: purchase.date.split('T')[0] // Добавляем дату закупки
    });
    navigate(`/supplier-payments?${params.toString()}`);
  }

  const formatDate = (dateStr: string) => {
    return formatDateUtil(dateStr)
  }

  const formatTime = (timeStr: string) => {
    return timeStr.slice(0, 5) // HH:MM
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'DRAFT': return 'Черновик'
      case 'POSTED': return 'Проведен'
      case 'CANCELLED': return 'Отменен'
      default: return 'Черновик'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT': return 'text-yellow-600'
      case 'POSTED': return 'text-green-600'
      case 'CANCELLED': return 'text-red-600'
      default: return 'text-yellow-600'
    }
  }

  const handleViewJson = (purchase: ServicePurchase) => {
    const jsonData = JSON.stringify(purchase, null, 2)
    setSelectedPurchaseJson(jsonData)
    setShowJsonModal(true)
  }

  const handleCloseJsonModal = () => {
    setShowJsonModal(false)
    setSelectedPurchaseJson('')
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Закупка услуг</h1>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/court-reservations')}
            className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
          >
            Резервирование кортов
          </button>
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Создать закупку
          </button>
        </div>
      </div>

      {/* Фильтры по датам и поставщику */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex-1 min-w-48">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Дата с
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => handleDateFromChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex-1 min-w-48">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Дата по
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => handleDateToChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex-1 min-w-48">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Поставщик
            </label>
            <select
              value={supplierFilter}
              onChange={(e) => handleSupplierFilterChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Все поставщики</option>
              {suppliers.map((supplier) => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleResetFilters}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 text-sm"
            >
              Сбросить
            </button>
            <button
              onClick={handleRefresh}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
            >
              Обновить
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">Загрузка...</div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Дата
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Поставщик
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Тип
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Турниры
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Часы
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Сумма
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Статус
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Действия
                            </th>
                          </tr>
                        </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {purchases.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                    Нет закупок услуг
                  </td>
                </tr>
              ) : (
                purchases
                  .map((purchase) => {
                  // Получаем уникальные турниры из позиций
                  const tournaments = [...new Set(purchase.items.map(item => item.tournament.name))]
                  const hasZeroCourtRate = purchase.items.some((item) => Number(item.ratePerHour || 0) === 0)
                  
                  return (
                    <tr key={purchase.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(purchase.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {purchase.supplier.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(purchase as any).type || '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {tournaments.join(', ')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                        {purchase.items.reduce((sum, item) => sum + item.hours, 0).toFixed(1)} ч
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${
                          hasZeroCourtRate ? 'text-red-700 font-semibold bg-red-50' : 'text-gray-900'
                        }`}
                        title={hasZeroCourtRate ? 'В документе есть позиция с нулевым тарифом корта' : undefined}
                      >
                        {purchase.totalAmount.toFixed(0)} MDL
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`text-sm font-medium ${getStatusColor(purchase.status)}`}>
                          {getStatusText(purchase.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex space-x-1">
                          <button
                            onClick={() => handleEdit(purchase)}
                            className="text-indigo-600 hover:text-indigo-900 text-sm"
                            title="Редактировать"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleCopy(purchase)}
                            className="text-green-600 hover:text-green-900 text-sm"
                            title="Создать копию"
                          >
                            📋
                          </button>
                          <button
                            onClick={() => handleViewJson(purchase)}
                            className="text-blue-600 hover:text-blue-900 text-sm"
                            title="Просмотр JSON"
                          >
                            📄
                          </button>
                          <button
                            onClick={() => handleCreateSupplierPayment(purchase)}
                            className="text-green-600 hover:text-green-900 text-sm"
                            title="Оплатить на основании"
                          >
                            💳
                          </button>
                          <button
                            onClick={() => handleDelete(purchase.id)}
                            className="text-red-600 hover:text-red-900 text-sm"
                            title="Удалить"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {!loading && pagination.pages > 1 && (
        <div className="flex justify-center items-center gap-2">
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
            disabled={pagination.page <= 1}
            className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Назад
          </button>
          <span className="px-4 py-2 text-sm text-gray-700">
            Страница {pagination.page} из {pagination.pages} (всего: {pagination.total})
          </span>
          <button
            onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.pages, prev.page + 1) }))}
            disabled={pagination.page >= pagination.pages}
            className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Вперед
          </button>
        </div>
      )}

      {showForm && (
        <ServicePurchaseForm
          purchase={editing}
          isCopying={isCopying}
          onClose={handleFormClose}
        />
      )}

      {/* Модальное окно для просмотра JSON */}
      {showJsonModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-4 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-2">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-base font-medium text-gray-900">
                  JSON данные документа "Закупка услуг"
                </h3>
                <button
                  onClick={handleCloseJsonModal}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                  title="Закрыть"
                >
                  ×
                </button>
              </div>
              <div className="bg-gray-100 rounded p-4 max-h-96 overflow-auto">
                <pre className="text-xs text-gray-800 whitespace-pre-wrap font-mono">
                  {selectedPurchaseJson}
                </pre>
              </div>
              <div className="mt-4 flex justify-end">
                <button
                  onClick={handleCloseJsonModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
                >
                  Закрыть
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Компонент формы закупки услуг
export const ServicePurchaseForm: React.FC<{
  purchase?: ServicePurchase | null
  isCopying?: boolean
  onClose: () => void
}> = ({ purchase, isCopying = false, onClose }) => {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().slice(0, 10),
    supplierId: '',
    type: 'Аренда кортов',
    totalAmount: 0,
    comment: '',
    status: 'POSTED',
    expenseArticleId: undefined as string | undefined,
    items: [] as Array<{
      id: string
      tournamentId: string
      courtId: string
      startTime: string
      endTime: string
      hours: number
      ratePerHour: number
      amount: number
      comment?: string
    }>
  })

  const [tournaments, setTournaments] = useState<Array<{ 
    id: string; 
    name: string; 
    courtRentalPrice: number; 
    clubId: string;
    tournamentClubs?: Array<{
      id: string;
      clubId: string;
      isPrimary: boolean;
      club: {
        id: string;
        name: string;
      };
    }>;
  }>>([])
  const [suppliers, setSuppliers] = useState<Array<{ id: string; name: string; clubId?: string; club?: { id: string; name: string } }>>([])
  const [courts, setCourts] = useState<Array<{ id: string; name: string }>>([])
  const [expenseArticles, setExpenseArticles] = useState<Array<{ id: string; code: string; name: string; type: string }>>([])
  const [loading, setLoading] = useState(false)
  const [initialized, setInitialized] = useState(false)
  const [lastLoadedTournamentId, setLastLoadedTournamentId] = useState<string | null>(null)

  const defaultCourtRentExpenseArticle = expenseArticles.find(article =>
    article.type === 'EXPENSE' &&
    (article.code === 'TN003' || article.name.trim().toLowerCase() === 'аренда кортов')
  )

  const loadRefs = async () => {
    try {
      const tournamentsData = await apiGet<{ tournaments?: any[] } | any[]>('/tournaments')
      setTournaments(Array.isArray(tournamentsData) ? tournamentsData : (tournamentsData.tournaments || []))

      const expenseArticlesData = await apiGet<any[]>('/expense-articles')
      setExpenseArticles(expenseArticlesData || [])
    } catch (e) {
      console.error('Ошибка загрузки справочников:', e)
    }
  }

  const loadProviders = useCallback(async () => {
    try {
      // Загружаем всех поставщиков
      const providersData = await apiGet<{ suppliers?: any[] }>('/suppliers?isActive=true&limit=1000')
      const providers = (providersData.suppliers || []).map((p: any) => ({
        id: p.id,
        name: p.name,
        clubId: p.clubId,
        club: p.club
      }))
      setSuppliers(providers)
    } catch (e) {
      console.error('Ошибка загрузки поставщиков:', e)
    }
  }, [])

  const loadTournamentsByProvider = useCallback(async (providerId: string) => {
    try {
      // Получаем поставщика для определения клуба
      const provider = suppliers.find(p => p.id === providerId)
      if (!provider) {
        console.log('Поставщик не найден:', providerId)
        return
      }
      
      // Для типа "Налоги" клуб не требуется, но мы загружаем турниры только если есть клуб
      if (!provider.clubId) {
        console.log('У поставщика нет клуба, турниры не требуются:', provider.name)
        setTournaments([])
        return
      }

      console.log('Загружаем турниры для клуба:', provider.clubId, 'поставщика:', provider.name)

      // Загружаем турниры клуба поставщика
      const tournamentsData = await apiGet<{ tournaments?: any[] } | any[]>(`/tournaments?clubId=${provider.clubId}`)
      const providerTournaments = Array.isArray(tournamentsData) ? tournamentsData : (tournamentsData.tournaments || [])

      // Если форма открыта из резервов, турнир в позиции уже может быть задан.
      // Гарантируем, что такие турниры появятся в списке даже если клубовый фильтр их не вернул.
      const prefilledTournamentIds = Array.from(new Set(
        ((purchase as any)?.items || [])
          .map((item: any) => String(item?.tournamentId || item?.tournament?.id || '').trim())
          .filter(Boolean)
      ))

      if (prefilledTournamentIds.length > 0) {
        const existingIds = new Set(providerTournaments.map((t: any) => String(t?.id || '').trim()))
        const missingIds = prefilledTournamentIds.filter(id => !existingIds.has(id))

        if (missingIds.length > 0) {
          const allTournamentsData = await apiGet<{ tournaments?: any[] } | any[]>('/tournaments?limit=1000')
          const allTournaments = Array.isArray(allTournamentsData) ? allTournamentsData : (allTournamentsData.tournaments || [])
          const missingTournaments = allTournaments.filter((t: any) => missingIds.includes(String(t?.id || '').trim()))
          providerTournaments.push(...missingTournaments)
        }
      }

      console.log('Загружены турниры:', providerTournaments.length)
      setTournaments(providerTournaments)
    } catch (e) {
      console.error('Ошибка загрузки турниров:', e)
    }
  }, [suppliers, purchase])

  // Загружаем поставщиков при инициализации
  useEffect(() => {
    loadProviders()
  }, [loadProviders])

  const loadCourts = useCallback(async (providerId: string) => {
    try {
      // Получаем поставщика для определения клуба
      const provider = suppliers.find(p => p.id === providerId)
      if (!provider || !provider.clubId) {
        // Для типа "Налоги" корты не требуются
        setCourts([])
        return
      }

      // Загружаем корты клуба поставщика
      const courtsData = await apiGet<{ items?: any[] }>(`/courts?clubId=${provider.clubId}`)
      setCourts(courtsData.items || [])
    } catch (e) {
      console.error('Ошибка загрузки кортов:', e)
    }
  }, [suppliers])

  // Загружаем корты и турниры при инициализации (только для типа "Аренда кортов")
  useEffect(() => {
    if (purchase && suppliers.length > 0) {
      const purchaseType = (purchase as any).type || ''
      // Для типа "Налоги" корты не требуются
      if (purchaseType === 'Налоги') {
        return
      }
      
      // Если поставщик передан по имени, но не по ID, ищем его в списке поставщиков
      let supplierId = purchase.supplier?.id || '';
      if (!supplierId && purchase.supplier?.name) {
        const foundSupplier = suppliers.find(s => s.name === purchase.supplier?.name);
        if (foundSupplier) {
          supplierId = foundSupplier.id;
        }
      }
      
      if (supplierId) {
        loadTournamentsByProvider(supplierId)
        loadCourts(supplierId)
      }
    }
  }, [purchase, suppliers, loadCourts, loadTournamentsByProvider])

  useEffect(() => {
    loadRefs()
  }, [])

  // Инициализируем данные при редактировании
  useEffect(() => {
    if (purchase && tournaments.length > 0 && suppliers.length > 0 && !initialized) {
      const isNewFromPrefill = !purchase.id
      // Если поставщик передан по имени, но не по ID, ищем его в списке поставщиков
      let supplierId = purchase.supplier?.id || '';
      console.log('Исходный поставщик из purchase:', purchase.supplier);
      console.log('Список поставщиков:', suppliers);
      
      if (!supplierId && purchase.supplier?.name) {
        const foundSupplier = suppliers.find(s => s.name === purchase.supplier?.name);
        console.log('Найденный поставщик по имени:', foundSupplier);
        if (foundSupplier) {
          supplierId = foundSupplier.id;
        }
      }
      
      console.log('Итоговый supplierId:', supplierId);

      setFormData({
        date: purchase.date ? new Date(purchase.date).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
        supplierId: supplierId,
        type: (purchase as any).type || 'Аренда кортов',
        totalAmount: purchase.totalAmount || 0,
        comment: purchase.comment || '',
        status: isCopying ? 'DRAFT' : (isNewFromPrefill ? 'POSTED' : (purchase.status || 'POSTED')),
        expenseArticleId: purchase.expenseArticle?.id || defaultCourtRentExpenseArticle?.id || undefined,
        items: purchase.items?.map(item => ({
          ...item,
          tournamentId: item.tournament?.id || ''
        })) || []
      })
      setInitialized(true)
    }
  }, [purchase, tournaments, suppliers, initialized, isCopying, defaultCourtRentExpenseArticle?.id])

  useEffect(() => {
    if (purchase || !defaultCourtRentExpenseArticle?.id) return
    setFormData(prev => ({
      ...prev,
      type: prev.type || 'Аренда кортов',
      status: prev.status || 'POSTED',
      expenseArticleId: prev.expenseArticleId || defaultCourtRentExpenseArticle.id
    }))
  }, [purchase, defaultCourtRentExpenseArticle?.id])

  useEffect(() => {
    if (!purchase || purchase.id || !defaultCourtRentExpenseArticle?.id) return
    setFormData(prev => ({
      ...prev,
      type: 'Аренда кортов',
      status: 'POSTED',
      expenseArticleId: prev.expenseArticleId || defaultCourtRentExpenseArticle.id
    }))
  }, [purchase, defaultCourtRentExpenseArticle?.id])


  // Сбрасываем инициализацию при смене документа
  useEffect(() => {
    if (purchase) {
      setInitialized(false)
      setLastLoadedTournamentId(null)
    }
  }, [purchase?.id, isCopying])

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: Date.now().toString(),
          tournamentId: '',
          courtId: '',
          startTime: '09:00',
          endTime: '11:00',
          hours: 2.0,
          ratePerHour: 0,
          amount: 0,
          comment: ''
        }
      ]
    }))
  }

  const updateItem = (index: number, field: string, value: any) => {
    setFormData(prev => {
      const newItems = [...prev.items]
      const item = { ...newItems[index] }

      if (field === 'tournamentId') {
        item.tournamentId = value
        // Устанавливаем тариф по умолчанию из турнира
        const selectedTournament = tournaments.find(t => t.id === value)
        if (selectedTournament) {
          item.ratePerHour = selectedTournament.courtRentalPrice || 0
          item.amount = Math.round(item.hours * item.ratePerHour)
        }
        // Сбрасываем корт при смене турнира
        item.courtId = ''
      } else if (field === 'courtId') {
        item.courtId = value
      } else if (field === 'startTime') {
        item.startTime = value
        item.endTime = calculateEndTime(value, item.hours)
      } else if (field === 'endTime') {
        item.endTime = value
        item.hours = calculateHours(item.startTime, value)
      } else if (field === 'hours') {
        item.hours = parseFloat(value) || 0
        item.endTime = calculateEndTime(item.startTime, item.hours)
      } else if (field === 'ratePerHour') {
        item.ratePerHour = parseFloat(value) || 0
      } else if (field === 'comment') {
        item.comment = value
      }

      // Пересчитываем сумму
      item.amount = Math.round(item.hours * item.ratePerHour)

      newItems[index] = item
      return { ...prev, items: newItems }
    })
  }

  const removeItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }))
  }

  const calculateHours = (startTime: string, endTime: string): number => {
    const [startHour, startMin] = startTime.split(':').map(Number)
    const [endHour, endMin] = endTime.split(':').map(Number)
    
    const startMinutes = startHour * 60 + startMin
    const endMinutes = endHour * 60 + endMin
    
    return Math.round(((endMinutes - startMinutes) / 60) * 10) / 10
  }

  const calculateEndTime = (startTime: string, hours: number): string => {
    const [startHour, startMin] = startTime.split(':').map(Number)
    const startMinutes = startHour * 60 + startMin
    const endMinutes = startMinutes + (hours * 60)
    
    const endHour = Math.floor(endMinutes / 60)
    const endMin = Math.floor(endMinutes % 60)
    
    return `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      // Если тип "Аренда кортов", проверяем наличие позиций
      if (formData.type === 'Аренда кортов') {
        if (formData.items.length === 0) {
          alert('Для типа "Аренда кортов" необходимо добавить хотя бы одну позицию')
          setLoading(false)
          return
        }
        
        // Проверяем, что все позиции имеют турнир
        const itemsWithoutTournament = formData.items.filter(item => !item.tournamentId)
        if (itemsWithoutTournament.length > 0) {
          alert('Все позиции должны иметь выбранный турнир')
          setLoading(false)
          return
        }
      }

      // Если тип "Налоги", проверяем наличие суммы
      if (formData.type === 'Налоги') {
        if (!formData.totalAmount || formData.totalAmount <= 0) {
          alert('Для типа "Налоги" необходимо указать сумму документа')
          setLoading(false)
          return
        }
      }

      // Рассчитываем сумму: для "Аренда кортов" - из позиций, для "Налоги" - из поля
      const calculatedTotalAmount = formData.type === 'Аренда кортов' 
        ? formData.items.reduce((sum, item) => sum + item.amount, 0)
        : formData.totalAmount || 0

      const payload = {
        ...formData,
        totalAmount: calculatedTotalAmount,
        items: formData.items.map(item => ({
          tournamentId: item.tournamentId,
          courtId: item.courtId,
          startTime: item.startTime,
          endTime: item.endTime,
          hours: item.hours,
          ratePerHour: item.ratePerHour,
          comment: item.comment
        }))
      }

      if (purchase && purchase.id) {
        await apiPut(`/service-purchases/${purchase.id}`, payload)
      } else {
        await apiPost('/service-purchases', payload)
      }

      onClose()
    } catch (e) {
      console.error('Ошибка сохранения:', e)
      alert('Ошибка сохранения')
    } finally {
      setLoading(false)
    }
  }

  // Рассчитываем итоги: для "Аренда кортов" - из позиций, для "Налоги" - из поля
  const totalAmount = formData.type === 'Аренда кортов' 
    ? formData.items.reduce((sum, item) => sum + item.amount, 0)
    : formData.totalAmount || 0
  const totalHours = formData.items.reduce((sum, item) => sum + item.hours, 0)

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">
              {purchase && !isCopying ? 'Редактировать закупку услуг' : 
               isCopying ? 'Создать копию закупки услуг' : 'Создать закупку услуг'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Шапка документа */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Дата *
                            </label>
                            <input
                              type="date"
                              required
                              value={formData.date}
                              onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                              className="w-full border border-gray-300 rounded-md px-3 py-2"
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Поставщик *
                            </label>
                            <select
                              required
                              value={formData.supplierId}
                              onChange={(e) => {
                                const providerId = e.target.value
                                setFormData(prev => ({ ...prev, supplierId: providerId }))
                                // Загружаем турниры и корты только для типа "Аренда кортов"
                                if (providerId && formData.type === 'Аренда кортов') {
                                  // Небольшая задержка, чтобы поставщики успели загрузиться
                                  setTimeout(() => {
                                    loadTournamentsByProvider(providerId)
                                    loadCourts(providerId)
                                  }, 100)
                                }
                              }}
                              className="w-full border border-gray-300 rounded-md px-3 py-2"
                            >
                              <option value="">Выберите поставщика</option>
                              {suppliers.map(p => (
                                <option key={p.id} value={p.id}>
                                  {p.name}{p.club ? ` 🏢 ${p.club.name}` : ''}
                                </option>
                              ))}
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Тип
                            </label>
                            <select
                              value={formData.type}
                              onChange={(e) => {
                                const newType = e.target.value
                                // Если тип изменяется на другой (не "Аренда кортов"), очищаем позиции
                                if (newType !== 'Аренда кортов') {
                                  setFormData(prev => ({ ...prev, type: newType, items: [], totalAmount: newType === 'Налоги' ? prev.totalAmount : 0 }))
                                } else {
                                  setFormData(prev => ({ ...prev, type: newType, totalAmount: 0 }))
                                  // Если переключаемся на "Аренда кортов" и выбран поставщик, загружаем турниры и корты
                                  if (formData.supplierId) {
                                    setTimeout(() => {
                                      loadTournamentsByProvider(formData.supplierId)
                                      loadCourts(formData.supplierId)
                                    }, 100)
                                  }
                                }
                              }}
                              className="w-full border border-gray-300 rounded-md px-3 py-2"
                            >
                              <option value="">Выберите тип</option>
                              <option value="Аренда кортов">Аренда кортов</option>
                              <option value="Налоги">Налоги</option>
                            </select>
                          </div>

                          {/* Поле суммы - показываем только для типа "Налоги" */}
                          {formData.type === 'Налоги' && (
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                Сумма документа *
                              </label>
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                required={formData.type === 'Налоги'}
                                value={formData.totalAmount || ''}
                                onChange={(e) => setFormData(prev => ({ ...prev, totalAmount: parseFloat(e.target.value) || 0 }))}
                                className="w-full border border-gray-300 rounded-md px-3 py-2"
                                placeholder="0.00"
                              />
                            </div>
                          )}

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Статус
                            </label>
                            <select
                              value={formData.status}
                              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                              className="w-full border border-gray-300 rounded-md px-3 py-2"
                            >
                              <option value="DRAFT">Черновик</option>
                              <option value="POSTED">Проведен</option>
                              <option value="CANCELLED">Отменен</option>
                            </select>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Статья затрат
                            </label>
                            <select
                              value={formData.expenseArticleId || ''}
                              onChange={(e) => setFormData(prev => ({ ...prev, expenseArticleId: e.target.value || undefined }))}
                              className="w-full border border-gray-300 rounded-md px-3 py-2"
                            >
                              <option value="">Выберите статью затрат</option>
                              {expenseArticles
                                .filter(article => article.type === 'EXPENSE')
                                .map(article => (
                                  <option key={article.id} value={article.id}>
                                    {article.code} - {article.name}
                                  </option>
                                ))}
                            </select>
                          </div>

                          <div className="md:col-span-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Комментарий
                            </label>
                            <textarea
                              value={formData.comment}
                              onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
                              rows={2}
                              className="w-full border border-gray-300 rounded-md px-3 py-2"
                            />
                          </div>
                        </div>

            {/* Табличная часть - показываем только для типа "Аренда кортов" */}
            {formData.type === 'Аренда кортов' && (
            <div>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium">Позиции</h3>
                <button
                  type="button"
                  onClick={addItem}
                  disabled={!formData.type}
                  className={`px-4 py-2 rounded ${
                    formData.type
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  Добавить позицию
                </button>
              </div>

              {formData.items.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  Добавьте позиции для закупки услуг
                </div>
              ) : (
                <div className="overflow-x-auto">
                              <table className="min-w-full border border-gray-200 text-xs">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Турнир *</th>
                                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Корт</th>
                                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Начало</th>
                                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Окончание</th>
                                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Часов</th>
                                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Тариф/час</th>
                                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Сумма</th>
                                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Комментарий</th>
                                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase">Действия</th>
                                  </tr>
                                </thead>
                    <tbody className="divide-y divide-gray-200">
                      {formData.items.map((item, index) => (
                        <tr key={item.id}>
                          <td className="px-2 py-1">
                            <select
                              required
                              value={item.tournamentId || ''}
                              onChange={(e) => updateItem(index, 'tournamentId', e.target.value)}
                              className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs"
                            >
                              <option value="">Выберите турнир</option>
                              {tournaments.map(t => {
                                const clubNames = t.tournamentClubs && t.tournamentClubs.length > 0 
                                  ? t.tournamentClubs.map(tc => tc.club.name).join(', ')
                                  : t.clubId ? 'Клуб не найден' : 'Без клуба';
                                return (
                                  <option key={t.id} value={t.id}>
                                    {t.name} {clubNames && `(${clubNames})`}
                                  </option>
                                );
                              })}
                            </select>
                          </td>
                          <td className="px-2 py-1">
                            <select
                              required
                              value={item.courtId || ''}
                              onChange={(e) => updateItem(index, 'courtId', e.target.value)}
                              className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs"
                            >
                              <option value="">Выберите корт</option>
                              {courts.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="time"
                              value={item.startTime || ''}
                              onChange={(e) => updateItem(index, 'startTime', e.target.value)}
                              className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="time"
                              value={item.endTime || ''}
                              onChange={(e) => updateItem(index, 'endTime', e.target.value)}
                              className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="number"
                              step="0.1"
                              value={item.hours || ''}
                              onChange={(e) => updateItem(index, 'hours', e.target.value)}
                              className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="number"
                              step="0.01"
                              value={item.ratePerHour || ''}
                              onChange={(e) => updateItem(index, 'ratePerHour', e.target.value)}
                              className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <span className="font-medium text-xs">{item.amount || 0} MDL</span>
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="text"
                              value={item.comment || ''}
                              onChange={(e) => updateItem(index, 'comment', e.target.value)}
                              className="w-full border border-gray-300 rounded px-1 py-0.5 text-xs"
                              placeholder="Комментарий"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="text-red-600 hover:text-red-800 text-xs px-1 py-0.5 rounded"
                            >
                              Удалить
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td colSpan={4} className="px-2 py-1 text-right font-medium text-xs">
                          Итого:
                        </td>
                        <td className="px-2 py-1 font-bold text-xs text-center">
                          {totalHours} ч
                        </td>
                        <td className="px-2 py-1 font-bold text-xs">
                          {totalAmount} MDL
                        </td>
                        <td colSpan={2}></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
            )}
            
            {/* Сообщение, если тип не выбран или не "Аренда кортов" */}
            {formData.type && formData.type !== 'Аренда кортов' && formData.type !== 'Налоги' && (
              <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                <p className="text-sm text-blue-800">
                  Для типа "{formData.type}" табличная часть с кортами не требуется.
                </p>
              </div>
            )}
            
            {!formData.type && (
              <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-sm text-yellow-800">
                  Выберите тип закупки услуг для добавления позиций.
                </p>
              </div>
            )}

            {/* Кнопки */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Отмена
              </button>
              <button
                type="submit"
                disabled={
                  loading || 
                  (formData.type === 'Аренда кортов' && formData.items.length === 0) ||
                  (formData.type === 'Налоги' && (!formData.totalAmount || formData.totalAmount <= 0)) ||
                  !formData.type ||
                  !formData.supplierId
                }
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default ServicePurchases
